import { ReactNode, useEffect } from 'react'
import { HiOutlineXMark } from 'react-icons/hi2'

type Props = {
  open: boolean
  title: string
  size?: 'sm' | 'md' | 'lg'
  children: ReactNode
  onClose: () => void
}

const sizeClass = { sm: 'max-w-md', md: 'max-w-lg', lg: 'max-w-2xl' } as const

export default function InfoDialog({ open, title, size = 'md', children, onClose }: Props) {
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = ''
    }
  }, [open, onClose])

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 font-sans"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div className={`w-full ${sizeClass[size]} overflow-hidden rounded-xl bg-white shadow-xl`}>
        <div className="flex items-start justify-between gap-4 px-6 pt-5">
          <h2 className="font-display text-lg font-semibold text-midnight-green">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            aria-label="Close"
          >
            <HiOutlineXMark className="h-5 w-5" />
          </button>
        </div>
        <div className="px-6 pb-6 pt-3 font-display text-sm leading-relaxed text-gray-600">
          {children}
        </div>
      </div>
    </div>
  )
}
